import { type ExtractedChartPage, withBroughtFiles } from "./extraction";
import type { EmrPageCapture } from "./handoff";
import { chrBroughtFiles, parseTelusChrPage } from "./telusChr";

/** Who may register a patient from another EMR's chart page: the chart importer's roles. */
const EMR_PAGE_IMPORT_ROLES: ReadonlySet<string> = new Set(["physician", "clinic_admin"]);

/** Whether this build offers the import, and this member may use it. */
export function canImportEmrPage(
  config: { readonly emrPageImportEnabled?: boolean },
  role: string | undefined,
): boolean {
  return (
    config.emrPageImportEnabled === true && role !== undefined && EMR_PAGE_IMPORT_ROLES.has(role)
  );
}

/** Why a page that lists files arrived without them. */
const FILES_LEFT_BEHIND = {
  paste:
    "A pasted chart carries only the files' names. Send it with the Send to BobDoc bookmark to bring the files.",
  bookmark:
    "This Send to BobDoc bookmark is older than bringing files. Drag the button in BobDoc's import box to your bookmarks bar again, then send the chart again.",
} as const;

/**
 * Reads a captured page with its EMR's parser, with the patient's files the
 * bookmark brought. The page's HTML is not kept.
 */
export function readCapture(capture: EmrPageCapture): ExtractedChartPage {
  switch (capture.source) {
    case "telus-chr": {
      const page = parseTelusChrPage(capture.html, { capturedAt: capture.capturedAt });
      if (capture.files !== undefined)
        return withBroughtFiles(page, chrBroughtFiles(capture.files));
      if (!page.notImported.some((item) => item.listedFile)) return page;
      return withBroughtFiles(page, { failed: FILES_LEFT_BEHIND[capture.via] });
    }
  }
}

/** Whether the page held a patient chart at all: a name, a birth date or a chart entry. */
export function foundChart(page: ExtractedChartPage): boolean {
  const { demographics } = page;
  return (
    demographics.legalLastName !== undefined ||
    demographics.dateOfBirth !== undefined ||
    page.healthCard !== undefined ||
    page.medications.length +
      page.allergies.length +
      page.problems.length +
      page.familyHistory.length +
      page.socialHistory.length +
      page.immunizations.length +
      page.labs.length >
      0
  );
}

/**
 * A chart the bookmark's tab took while no BobDoc tab was waiting, kept in this
 * tab's memory while the physician signs in to register the patient here. Never
 * in the URL, history state or browser storage.
 */
let held: EmrPageCapture | null = null;

export function holdCapture(capture: EmrPageCapture): void {
  held = capture;
}

/** The held chart, left in place: React may render the page that reads it more than once. */
export function heldCapture(): EmrPageCapture | null {
  return held;
}

/** Drops the held chart once the page that registers from it has it. */
export function releaseHeldCapture(): void {
  held = null;
}
